import OpenAI from 'openai';
import { getOpenAIKey, config } from './config';
import type { ChatMessage } from './chatbot-api';

const openai = new OpenAI({
  apiKey: getOpenAIKey(),
  dangerouslyAllowBrowser: true // Only for demo - in production, use backend
});

export interface ChatContext {
  language?: string;
  customerName?: string;
  country?: string;
  checkIn?: string;
  checkOut?: string;
}

export async function getChatCompletion(
  messages: ChatMessage[],
  context: ChatContext
): Promise<string> {
  try {
    const systemPrompt = buildSystemPrompt(context);
    
    const openaiMessages: OpenAI.Chat.ChatCompletionMessageParam[] = [
      { role: 'system', content: systemPrompt },
      ...messages.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content
      }))
    ];

    const completion = await openai.chat.completions.create({
      model: config.openai.model,
      messages: openaiMessages,
      max_tokens: config.openai.maxTokens,
      temperature: config.openai.temperature,
    });

    return completion.choices[0]?.message?.content || 'I apologize, but I encountered an error. Please try again.';
  } catch (error) {
    console.error('OpenAI API Error:', error);
    return 'I apologize, but I am having trouble connecting right now. Please try again later or contact us directly.';
  }
}

function buildSystemPrompt(context: ChatContext): string {
  const basePrompt = `You are ${config.chatbot.name}, a helpful and friendly booking assistant for Pera House, a luxury villa rental in Lindos, Rhodes, Greece.

PROPERTY DETAILS:
- Two beautiful villas in Lindos village
- Located near the ancient Acropolis
- Walking distance to pristine beaches
- Traditional Greek architecture with modern amenities
- Breathtaking Aegean Sea views
- Perfect for families and couples
- Full kitchen, WiFi, air conditioning, private terraces

YOUR ROLE:
1. Help customers with booking inquiries
2. Provide information about the villas and location
3. Collect booking details: name, country, check-in/out dates, email, phone
4. Be warm, professional, and enthusiastic about the property
5. Suggest nearby attractions and activities when appropriate

CONVERSATION FLOW:
${!context.language ? '- First, ask what language they prefer (English, Greek, German, French, Spanish)' : `- Speaking in ${context.language}`}
${!context.customerName ? '- Then ask for their name to personalize the conversation' : `- Customer name: ${context.customerName}`}
${!context.country ? '- Ask which country they are from' : `- From: ${context.country}`}
${!context.checkIn || !context.checkOut ? '- Ask for their desired check-in and check-out dates' : `- Dates: ${context.checkIn} to ${context.checkOut}`}
- Finally, ask for email and phone number to complete the booking inquiry

Keep responses concise (2-3 sentences max). Be conversational and friendly.`;

  return basePrompt;
}

export function generateSessionId(): string {
  return `chat_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
